import { Button } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Otp } from 'BreetComponents';
import { type IApiResponse, type IKybDetails, isFetchBaseQueryError, OTPLENGTH, showToast } from 'BreetHelpers';
import { useOtpApi } from 'BreetHooks';
import {
	closeModal,
	kybAuthAction,
	kybAuthSelectors,
	useAppDispatch,
	useAppSelector,
	useLazyGetKybDetailsQuery,
	useVerifyKybIdentityMutation,
} from 'BreetRedux';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { KybFormDataAuthOTPSchema, type KybFormDataAuthOTPSchemaType } from '../../../Kyb.helpers';

const KybOTPForm = () => {
	const dispatch = useAppDispatch();

	const [verifyKybIdentity, { isLoading }] = useVerifyKybIdentityMutation();

	const [getKybDetails, { isLoading: isLoadingGetKybDetails }] = useLazyGetKybDetailsQuery();

	const navigate = useNavigate();

	const { otpRef, resetOtpInputValues } = useOtpApi();

	const { setKybCredentials, setFormData } = kybAuthAction;

	const { selectKybCredentials } = kybAuthSelectors;

	const credentials = useAppSelector(selectKybCredentials);

	const {
		setValue,
		setError,
		clearErrors,
		handleSubmit,
		formState: { errors },
	} = useForm<KybFormDataAuthOTPSchemaType>({
		resolver: yupResolver(KybFormDataAuthOTPSchema),
	});

	const handleDispatch = useCallback(
		(email: string, token: string, data?: Partial<IKybDetails>) => {
			dispatch(setKybCredentials({ token, email }));
			if (data) {
				dispatch(setFormData(data));
			}
			dispatch(closeModal('registerKYBOTP'));
		},
		[dispatch, setFormData, setKybCredentials]
	);

	const handleNavigate = useCallback(
		(data?: Partial<IKybDetails>) => {
			const path =
				data?.status === 'pending' || data?.status === 'completed' ? '/register-business/status' : '/register-business/about';
			navigate(path)?.catch(console.error);
		},
		[navigate]
	);

	const verifyOTP = useCallback(
		(otp?: string, restartOTPResendCountDown?: () => void) => {
			try {
				const email = credentials.email;
				if (!email) {
					showToast('error', 'email not found');
					return;
				}
				const isOTP = typeof otp === 'string';
				const payload = isOTP ? { email, otp } : { email };
				verifyKybIdentity(payload)
					.unwrap()
					.then(({ data }) => {
						if (!isOTP) {
							restartOTPResendCountDown?.();
							return;
						}

						if (!data?.token) {
							showToast('error', 'Verification failed. Please try again.');
							return;
						}

						return getKybDetails({ token: data.token })
							.unwrap()
							.then((res) => {
								handleDispatch(email, data.token, res.data);
								handleNavigate(res.data);
							})
							.catch((err: unknown) => {
								if (isFetchBaseQueryError(err) && err.status === 404) {
									// 404 = first-time user, no KYB record yet - proceed to registration
									handleDispatch(email, data.token);
									handleNavigate();
								} else {
									// All other errors (5xx, network errors, etc.) - show error to user
									showToast('error', 'Failed to retrieve account details. Please try again.');
								}
							});
					})
					.catch((e: unknown) => {
						const errmrData = e as { data: IApiResponse } | undefined;
						setError('otp', { message: `${errmrData?.data.message}.` });
					});
			} catch (error) {
				console.error(error);
			}
		},
		[credentials.email, getKybDetails, handleDispatch, handleNavigate, setError, verifyKybIdentity]
	);

	const resendOTP = useCallback(
		(restartCountDown?: () => void) => {
			verifyOTP(undefined, restartCountDown);
			resetOtpInputValues();
			clearErrors('otp');
		},
		[clearErrors, resetOtpInputValues, verifyOTP]
	);

	const handleOTPVerification = useCallback(
		(otpArg: { otp: string }) => {
			verifyOTP(otpArg.otp);
			clearErrors('otp');
		},
		[clearErrors, verifyOTP]
	);

	const handleOTPValue = useCallback(
		(otpValue: string) => {
			setValue('otp', otpValue);

			if (otpValue.length === OTPLENGTH) {
				verifyOTP(otpValue);
				clearErrors('otp');
			}
		},
		[clearErrors, setValue, verifyOTP]
	);

	return (
		<form
			className='registerYourBusinessAuth_otp_form'
			onSubmit={handleSubmit(handleOTPVerification)}
		>
			<Otp.Input
				onChange={handleOTPValue}
				otpLength={OTPLENGTH}
				error={errors.otp?.message}
				data-testid='otp-input'
				ref={otpRef}
				maskInputValue
			/>

			<Otp.Resend
				onResend={resendOTP}
				isLoading={isLoading || isLoadingGetKybDetails}
				data-testid='resend-otp'
				errorMessage={errors.otp?.message}
			/>

			<div className='registerYourBusinessAuth_otp_form_cta'>
				<Button
					label='Continue'
					disabled={!!errors.otp?.message || isLoading || isLoadingGetKybDetails}
					isLoading={isLoading || isLoadingGetKybDetails}
					type='submit'
				/>
			</div>
		</form>
	);
};

export default KybOTPForm;
